import React from 'react';
import { Card, Badge } from '@medikei/ui';
import { Users, UserCheck, Sparkles, Check } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Checkbox } from '@/components/ui/checkbox';

export interface DoctorTargetAudienceSectionProps {
  minAge?: number | null;
  maxAge?: number | null;
  allowedAgeGroups?: string[];
  onChange: (data: {
    minAge?: number | null;
    maxAge?: number | null;
    allowedAgeGroups?: string[];
  }) => void;
  className?: string;
}

export interface AgeGroupItem {
  key: string;
  label: string;
  rangeLabel: string;
  min: number;
  max: number;
  description: string;
}

export const CLINICAL_AGE_GROUPS: AgeGroupItem[] = [
  {
    key: 'NEWBORN',
    label: 'Bebês / Lactentes',
    rangeLabel: '0 a 2 anos',
    min: 0,
    max: 2,
    description: 'Recém-nascidos e primeira infância',
  },
  {
    key: 'CHILD',
    label: 'Crianças',
    rangeLabel: '2 a 11 anos',
    min: 2,
    max: 11,
    description: 'Infância e idade pré-escolar/escolar',
  },
  {
    key: 'ADOLESCENT',
    label: 'Adolescentes',
    rangeLabel: '12 a 17 anos',
    min: 12,
    max: 17,
    description: 'Puberdade e transição juvenil',
  },
  {
    key: 'ADULT',
    label: 'Adultos',
    rangeLabel: '18 a 59 anos',
    min: 18,
    max: 59,
    description: 'Adultos jovens e meia-idade',
  },
  {
    key: 'ELDERLY',
    label: 'Idosos',
    rangeLabel: '60+ anos',
    min: 60,
    max: 120,
    description: 'Terceira idade e cuidados geriátricos',
  },
];

interface PresetConfig {
  id: string;
  title: string;
  subtitle: string;
  minAge: number;
  maxAge: number;
  allowedAgeGroups: string[];
}

const PRESETS: PresetConfig[] = [
  {
    id: 'all',
    title: 'Todas as Idades (Livre)',
    subtitle: 'De recém-nascidos a idosos',
    minAge: 0,
    maxAge: 120,
    allowedAgeGroups: ['NEWBORN', 'CHILD', 'ADOLESCENT', 'ADULT', 'ELDERLY'],
  },
  {
    id: 'pediatrics',
    title: 'Pediatria (0 a 17 anos)',
    subtitle: 'Bebês, crianças e adolescentes',
    minAge: 0,
    maxAge: 17,
    allowedAgeGroups: ['NEWBORN', 'CHILD', 'ADOLESCENT'],
  },
  {
    id: 'adults',
    title: 'Adultos e Idosos (18+ anos)',
    subtitle: 'Adultos e terceira idade',
    minAge: 18,
    maxAge: 120,
    allowedAgeGroups: ['ADULT', 'ELDERLY'],
  },
];

export function DoctorTargetAudienceSection({
  minAge,
  maxAge,
  allowedAgeGroups = [],
  onChange,
  className,
}: DoctorTargetAudienceSectionProps) {
  const currentMin = typeof minAge === 'number' ? minAge : 0;
  const currentMax = typeof maxAge === 'number' ? maxAge : 120;
  const currentGroups = allowedAgeGroups || [];

  const handleApplyPreset = (preset: PresetConfig) => {
    onChange({
      minAge: preset.minAge,
      maxAge: preset.maxAge,
      allowedAgeGroups: [...preset.allowedAgeGroups],
    });
  };

  const handleToggleGroup = (groupKey: string) => {
    const isSelected = currentGroups.includes(groupKey);
    const newGroups = isSelected
      ? currentGroups.filter((g) => g !== groupKey)
      : [...currentGroups, groupKey];

    onChange({
      minAge: currentMin,
      maxAge: currentMax,
      allowedAgeGroups: newGroups,
    });
  };

  const handleMinAgeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
    const parsed = isNaN(val as number) ? null : val;
    onChange({
      minAge: parsed,
      maxAge,
      allowedAgeGroups: currentGroups,
    });
  };

  const handleMaxAgeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
    const parsed = isNaN(val as number) ? null : val;
    onChange({
      minAge,
      maxAge: parsed,
      allowedAgeGroups: currentGroups,
    });
  };

  const isPresetActive = (preset: PresetConfig) => {
    if (currentMin !== preset.minAge || currentMax !== preset.maxAge) {
      return false;
    }
    if (currentGroups.length !== preset.allowedAgeGroups.length) {
      return false;
    }
    return preset.allowedAgeGroups.every((g) => currentGroups.includes(g));
  };

  const isInvalidRange = currentMin > currentMax;

  return (
    <Card
      id="publico-atendido"
      className={cn('p-6 bg-card border-border scroll-mt-6 space-y-6', className)}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2">
          <Users className="text-primary-500 h-5 w-5 shrink-0" />
          <h3 className="font-semibold text-foreground text-lg">
            Público Atendido e Faixas Etárias
          </h3>
        </div>
        <Badge
          variant="outline"
          className="w-fit bg-primary-500/10 text-primary-600 border-primary-500/30 text-xs font-medium"
        >
          Habilitação de Atendimento
        </Badge>
      </div>

      <p className="text-sm text-muted-foreground -mt-2">
        Defina os limites de idade dos pacientes que você está habilitado e disponível para atender.
      </p>

      {/* Real-time Summary Badge */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-primary-500/10 border border-primary-500/20 rounded-xl">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-500 text-white rounded-lg shadow-sm">
            <UserCheck className="h-4 w-4" />
          </div>
          <div>
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Resumo da Capacidade
            </div>
            <div className="text-sm font-semibold text-foreground">
              Configuração atual: Pacientes de {currentMin} a {currentMax} anos
            </div>
          </div>
        </div>
        <Badge
          className={cn(
            'text-xs font-medium px-3 py-1 w-fit',
            isInvalidRange
              ? 'bg-destructive text-destructive-foreground'
              : 'bg-primary-500 text-white'
          )}
        >
          {isInvalidRange
            ? 'Intervalo Inválido'
            : currentMin === 0 && currentMax >= 120
            ? 'Atendimento Completo'
            : currentMax <= 17
            ? 'Público Pediátrico'
            : currentMin >= 18
            ? 'Público Adulto/Geriátrico'
            : 'Faixa Customizada'}
        </Badge>
      </div>

      {isInvalidRange && (
        <p className="text-xs font-medium text-destructive">
          A idade mínima ({currentMin} anos) não pode ser maior que a idade máxima ({currentMax} anos).
        </p>
      )}

      {/* Quick Presets */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          <Sparkles className="h-3.5 w-3.5 text-amber-500" />
          Atalhos Rápidos de Configuração
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {PRESETS.map((preset) => {
            const active = isPresetActive(preset);
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => handleApplyPreset(preset)}
                className={cn(
                  'flex flex-col items-start p-3.5 rounded-xl border text-left transition-all duration-200 group',
                  active
                    ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-950/30 ring-2 ring-primary-500/20 shadow-sm'
                    : 'border-border bg-card hover:border-primary-300 dark:hover:border-primary-700 hover:bg-muted/40'
                )}
              >
                <div className="flex items-center justify-between w-full mb-1">
                  <span
                    className={cn(
                      'text-sm font-semibold transition-colors',
                      active ? 'text-primary-600 dark:text-primary-400' : 'text-foreground'
                    )}
                  >
                    {preset.title}
                  </span>
                  {active && (
                    <span className="flex items-center justify-center h-4 w-4 rounded-full bg-primary-500 text-white">
                      <Check className="h-2.5 w-2.5 stroke-[3]" />
                    </span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">{preset.subtitle}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Clinical Age Groups Selection */}
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-semibold text-foreground">
            Faixas Etárias Clínicas Atendidas
          </label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Selecione quais categorias clínicas têm cobertura na sua rotina de consultas:
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {CLINICAL_AGE_GROUPS.map((group) => {
            const isSelected = currentGroups.includes(group.key);
            return (
              <label
                key={group.key}
                htmlFor={`group-${group.key}`}
                className={cn(
                  'flex items-start gap-3 p-3 rounded-xl border cursor-pointer select-none transition-all duration-150',
                  isSelected
                    ? 'border-primary-500 bg-primary-50/40 dark:bg-primary-950/30'
                    : 'border-border bg-card hover:bg-muted/30'
                )}
              >
                <Checkbox
                  id={`group-${group.key}`}
                  checked={isSelected}
                  onCheckedChange={() => handleToggleGroup(group.key)}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-1">
                    <span
                      className={cn(
                        'text-sm font-medium leading-none',
                        isSelected ? 'text-primary-900 dark:text-primary-100 font-semibold' : 'text-foreground'
                      )}
                    >
                      {group.label}
                    </span>
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-[10px] px-1.5 py-0 h-4 border leading-none shrink-0',
                        isSelected
                          ? 'border-primary-300 text-primary-600 dark:border-primary-700 dark:text-primary-300'
                          : 'text-muted-foreground'
                      )}
                    >
                      {group.rangeLabel}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                    {group.description}
                  </p>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Numeric Fine Tuning */}
      <div className="space-y-3 pt-2 border-t border-border">
        <div>
          <label className="block text-sm font-semibold text-foreground">
            Ajuste Fino Numérico de Idade
          </label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Especifique os limites numéricos precisos para validação automática durante o agendamento de consultas.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label
              htmlFor="min-age-input"
              className="block text-xs font-medium text-foreground"
            >
              Idade Mínima (anos)
            </label>
            <div className="relative">
              <input
                id="min-age-input"
                type="number"
                min={0}
                max={120}
                value={minAge !== null && minAge !== undefined ? minAge : ''}
                onChange={handleMinAgeChange}
                placeholder="0"
                className="w-full px-3 py-2 border rounded-lg bg-background border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                anos
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground">Ex: 0 para recém-nascidos, 18 para adultos.</p>
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="max-age-input"
              className="block text-xs font-medium text-foreground"
            >
              Idade Máxima (anos)
            </label>
            <div className="relative">
              <input
                id="max-age-input"
                type="number"
                min={0}
                max={120}
                value={maxAge !== null && maxAge !== undefined ? maxAge : ''}
                onChange={handleMaxAgeChange}
                placeholder="120"
                className="w-full px-3 py-2 border rounded-lg bg-background border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                anos
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground">Ex: 14 ou 17 para pediatria restrita, 120 para livre.</p>
          </div>
        </div>
      </div>
    </Card>
  );
}
